/**
 * ControlsImplementationPhase — Phase 1 (ISO 21434 Clause 10.1).
 *
 * For each control extracted from the base TARA, the user records
 * evidence of implementation (file uploads, notes, date, implementer)
 * via the existing `ControlImplementationModalEnhanced` from
 * `features/athena/components/`. Once every control is marked
 * implemented, the "Send to Requirements Evaluation" CTA at the
 * bottom calls `actions.continueWorkflow()` and the backend
 * transitions to `requirements_evaluation`.
 *
 * Read-only for any session past `controls_pending` (rail jump
 * lands users here to review their prior choices).
 */
import React, { useMemo, useState } from 'react';
import { Button } from '../../../../../components/ui/Button2';
import { Empty, EmptyHeader, EmptyTitle, EmptyDescription } from '../../../../../components/ui/Empty';
import { TypographyH4, TypographyMuted } from '../../../../../components/ui/Typography';
import StatusPill from '../../../../../components/ui/StatusPill/StatusPill';
import PreviewModal from '../../../../../components/ui/PreviewModal/PreviewModal';
import ControlImplementationModalEnhanced from '../../../../athena/components/ControlImplementationModalEnhanced';
import { useWorkflowSession } from '../../../../athena/contexts/WorkflowSessionContext';
import type { ResidualActions } from '../../../hooks/useResidualProgress';
import type { ResidualControl, ResidualState } from '../../../state/types';
import styles from './ControlsImplementationPhase.module.css';

// Button2 is a JSX forwardRef primitive; cast for use from TSX (mirrors the
// ButtonAny convention across the step-components).
const ButtonAny = Button as unknown as React.ComponentType<any>;

interface ControlsImplementationPhaseProps {
  state: ResidualState;
  actions: ResidualActions;
}

/** One user-uploaded evidence document (the shape saved by the
 *  Control Implementation modal). */
interface EvidenceDoc {
  filename: string;
  content?: string; // base64 (no data: prefix)
  file_type?: string; // mime
  file_size?: number; // bytes
  upload_date?: string;
  source?: string;
}

const isImplemented = (c: ResidualControl): boolean =>
  c.implementation_status === 'implemented';

/**
 * Pull uploaded evidence documents out of a control's `evidence`.
 * Backend writes it as an array of `{ filename, content, … }` objects
 * (or a JSON string of the same; older paths may write a single
 * object). GitHub-collected evidence items carry no `filename` and are
 * skipped — this is only the user-uploaded document list.
 */
const evidenceDocs = (evidence: unknown): EvidenceDoc[] => {
  let items: unknown = evidence;
  if (typeof items === 'string') {
    try {
      items = JSON.parse(items);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(items)) {
    items = items && typeof items === 'object' ? [items] : [];
  }
  return (items as unknown[]).filter(
    (it): it is EvidenceDoc =>
      !!it &&
      typeof it === 'object' &&
      typeof (it as Record<string, unknown>).filename === 'string' &&
      ((it as Record<string, unknown>).filename as string).length > 0,
  );
};

const formatBytes = (bytes?: number): string => {
  if (!bytes || bytes <= 0) return '—';
  const kb = bytes / 1024;
  return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(1)} MB`;
};

const fileExtOf = (filename: string): string => {
  const dot = filename.lastIndexOf('.');
  return dot >= 0 ? filename.slice(dot) : '';
};

/**
 * Map a saved evidence document + its parent control onto the shape
 * PreviewModal expects (flat fallback fields, base64 in `evidence_file`).
 */
const toPreviewFile = (doc: EvidenceDoc, control: ResidualControl) => {
  const ext = fileExtOf(doc.filename);
  return {
    id: doc.filename,
    evidence_file: doc.content,
    evidence_file_type: doc.file_type || '',
    fileName: doc.filename,
    fileSize: formatBytes(doc.file_size),
    fileType: ext.replace('.', '').toLowerCase() || 'file',
    fileExtension: ext,
    source: doc.source || 'Upload',
    uploadedBy: control.implemented_by || '—',
    lastUpdate: doc.upload_date || control.implementation_date,
    relatedControl: control.control_id,
  };
};

/** Trigger a browser download of a base64 evidence document. */
const downloadEvidenceFile = (pf: ReturnType<typeof toPreviewFile>) => {
  if (!pf.evidence_file) return;
  const link = document.createElement('a');
  link.href = `data:${pf.evidence_file_type || 'application/octet-stream'};base64,${pf.evidence_file}`;
  link.download = pf.fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export default function ControlsImplementationPhase({
  state,
  actions,
}: ControlsImplementationPhaseProps) {
  const workflowSession = useWorkflowSession();
  const sessionId = workflowSession?.backendSession?.sessionId ?? null;
  const tenantId = workflowSession?.backendSession?.tenantId ?? null;

  const controls = state.data?.controls ?? [];
  const editable = state.backendPhase === 'controls_pending';

  const [selectedControl, setSelectedControl] = useState<ResidualControl | null>(null);
  const [previewFile, setPreviewFile] = useState<ReturnType<typeof toPreviewFile> | null>(null);
  const [isContinuing, setIsContinuing] = useState(false);

  const stats = useMemo(() => {
    const total = controls.length;
    const implemented = controls.filter(isImplemented).length;
    return { total, implemented, pct: total > 0 ? Math.round((implemented / total) * 100) : 0 };
  }, [controls]);

  const allImplemented = stats.total > 0 && stats.implemented === stats.total;

  // On failure the error propagates to the modal (which surfaces it via
  // its own state) and the modal stays open — only success closes it.
  const handleSave = async (saveData: unknown) => {
    if (!selectedControl) return;
    await actions.updateControl(selectedControl.control_id, saveData);
    setSelectedControl(null);
  };

  const handleContinue = async () => {
    if (isContinuing) return;
    setIsContinuing(true);
    try {
      await actions.continueWorkflow();
    } finally {
      setIsContinuing(false);
    }
  };

  return (
    <div className={styles.phase}>
      {/* Header — title + progress chip */}
      <header className={styles.header}>
        <div className={styles.headerText}>
          <TypographyH4>Controls Implementation</TypographyH4>
          <TypographyMuted className={styles.subtitle}>
            Verify each control is in place by attaching evidence. Once every
            control is implemented, send the bundle to the requirements
            agent for re-evaluation.
          </TypographyMuted>
        </div>
        <div className={styles.progressBlock}>
          <span className={styles.progressLabel}>
            <strong>{stats.implemented}</strong> / {stats.total} implemented
          </span>
          <div className={styles.progressBar}>
            <div className={styles.progressFill} style={{ width: `${stats.pct}%` }} />
          </div>
        </div>
      </header>

      {/* Body — per-control rows */}
      {controls.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>No controls in scope</EmptyTitle>
            <EmptyDescription>
              No controls were extracted from this session&rsquo;s
              cybersecurity concept.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div className={styles.list}>
          {controls.map((c) => {
            const done = isImplemented(c);
            // A control can be marked implemented without attaching evidence.
            // Requirement evaluation judges evidence, so those controls produce
            // unverifiable verdicts — surface that here rather than letting the
            // mismatch appear later as an unexplained "Not Met".
            const evidencePending = done && evidenceDocs(c.evidence).length === 0;
            return (
              <div key={c.control_id} className={styles.card}>
                {/* Header — id + name + status pill (matches the
                    Requirements Review control header). */}
                <header className={styles.cardHeader}>
                  <span className={styles.controlId}>{c.control_id}</span>
                  {c.control_name && (
                    <span className={styles.controlName}>{c.control_name}</span>
                  )}
                  <StatusPill
                    tone={done ? 'success' : 'warning'}
                    label={done ? 'Implemented' : 'Pending'}
                    size="sm"
                  />
                  {evidencePending && (
                    <StatusPill
                      tone="warning"
                      label="Evidence pending"
                      size="sm"
                    />
                  )}
                </header>

                {/* Body — description, meta, and the action button. */}
                <div className={styles.cardBody}>
                  <div className={styles.bodyMain}>
                    {c.control_description && (
                      <div className={styles.controlDesc}>{c.control_description}</div>
                    )}
                    {done && c.implementation_date && (
                      <div className={styles.controlMeta}>
                        Implemented {new Date(c.implementation_date).toLocaleDateString()}
                        {c.implemented_by ? ` · ${c.implemented_by}` : ''}
                        {evidenceDocs(c.evidence).map((doc, i) => (
                          // Separator stays plain; the filename is an
                          // underlined button that opens the file preview.
                          <React.Fragment key={i}>
                            {' · '}
                            <button
                              type="button"
                              className={styles.evidenceDocLink}
                              onClick={() => setPreviewFile(toPreviewFile(doc, c))}
                            >
                              {doc.filename}
                            </button>
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className={styles.rowActions}>
                    <ButtonAny
                      variant={done ? 'outline' : 'default'}
                      onClick={() => setSelectedControl(c)}
                      disabled={!editable && !done}
                    >
                      {done ? 'View / Edit' : 'Mark as Implemented'}
                    </ButtonAny>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer — Send to Requirements Evaluation CTA */}
      {editable && (
        <footer className={styles.footer}>
          <ButtonAny
            size="lg"
            loading={isContinuing}
            onClick={handleContinue}
            disabled={!allImplemented || isContinuing}
          >
            {isContinuing
              ? 'Sending…'
              : allImplemented
                ? 'Send to Requirements Evaluation'
                : `Implement all controls to continue (${stats.implemented}/${stats.total})`}
          </ButtonAny>
        </footer>
      )}

      {/* Modal — reused as-is from the legacy page */}
      {selectedControl && (
        <ControlImplementationModalEnhanced
          isOpen={!!selectedControl}
          onClose={() => setSelectedControl(null)}
          control={selectedControl}
          onSave={handleSave}
          sessionId={sessionId}
          tenantId={tenantId}
        />
      )}

      {/* Evidence file preview — opened from the doc links in the meta row. */}
      {previewFile && (
        <PreviewModal
          file={previewFile}
          tenantId={tenantId}
          onClose={() => setPreviewFile(null)}
          onDownload={() => downloadEvidenceFile(previewFile)}
        />
      )}
    </div>
  );
}
